/**
 * RFI (Request for Information) Type Definitions
 *
 * Frontend types for RFI tracking system.
 * Matches backend schemas in schemas/rfis.py
 */

export type RFIStatus = 'draft' | 'open' | 'answered' | 'closed';
export type ImpactLevel = 'none' | 'minor' | 'moderate' | 'major' | 'unknown';
export type RFIFileType = 'question' | 'answer';

/**
 * Status transition rules (mirrors backend VALID_TRANSITIONS).
 * Used for UI validation before API call.
 */
export const VALID_RFI_TRANSITIONS: Record<RFIStatus, RFIStatus[]> = {
  draft: ['open'],
  open: ['answered', 'closed'],
  answered: ['closed'],
  closed: [],
};

/**
 * Impact level options for dropdowns
 */
export const IMPACT_LEVELS: ImpactLevel[] = ['none', 'minor', 'moderate', 'major', 'unknown'];

export const IMPACT_LEVEL_LABELS: Record<ImpactLevel, string> = {
  none: 'None',
  minor: 'Minor',
  moderate: 'Moderate',
  major: 'Major',
  unknown: 'Unknown',
};

/**
 * RFI status display config (label + badge class)
 */
export const RFI_STATUS_CONFIG: Record<RFIStatus, { label: string; className: string }> = {
  draft: { label: 'Draft', className: 'bg-gray-200 text-gray-800' },
  open: { label: 'Open', className: 'bg-blue-500 text-white' },
  answered: { label: 'Answered', className: 'bg-green-500 text-white' },
  closed: { label: 'Closed', className: 'bg-slate-600 text-white' },
};

// ============================================================================
// Request bodies
// ============================================================================

/**
 * Create RFI request (frontend → backend)
 */
export interface RFICreate {
  subject: string;
  question?: string | null;
  background?: string | null;
  drawing_reference?: string | null;
  spec_reference?: string | null;
  cost_impact?: ImpactLevel | null;
  schedule_impact?: ImpactLevel | null;
  assigned_to?: string | null;
  assigned_to_email?: string | null;
  assigned_to_org_id?: string | null;
  date_due?: string | null; // ISO date string
}

/**
 * Update RFI request (all fields optional for partial update)
 */
export interface RFIUpdate {
  subject?: string;
  question?: string | null;
  background?: string | null;
  drawing_reference?: string | null;
  spec_reference?: string | null;
  cost_impact?: ImpactLevel | null;
  schedule_impact?: ImpactLevel | null;
  assigned_to?: string | null;
  assigned_to_email?: string | null;
  assigned_to_org_id?: string | null;
  date_due?: string | null;
}

/**
 * Status transition request
 */
export interface RFIStatusUpdate {
  status: RFIStatus;
}

/**
 * Answer submission request (atomic: sets answer + answered_by + status='answered')
 */
export interface RFIAnswerSubmit {
  answer: string;
  answered_by?: string | null;
  answered_by_email?: string | null;
  answered_by_org_id?: string | null;
}

/**
 * Email send request
 */
export interface SendRFIEmailRequest {
  recipient_email: string;
  custom_note?: string | null;
  reply_to_override?: string | null;
}

// ============================================================================
// Response shapes
// ============================================================================

/**
 * RFI file attachment record.
 * Backend strips storage_path and tenant_id before returning.
 */
export interface RFIFile {
  id: string;
  rfi_id: string;
  file_type: RFIFileType;
  file_name: string;
  file_size: number;
  content_type: string;
  uploaded_by: string;
  created_at: string;
}

/**
 * Complete RFI object (backend → frontend)
 */
export interface RFI {
  id: string;
  tenant_id: string;
  project_id: string;
  rfi_number: string; // Auto-generated e.g. "RFI-001"
  subject: string;
  question?: string | null;
  background?: string | null;
  drawing_reference?: string | null;
  spec_reference?: string | null;
  cost_impact?: ImpactLevel | null;
  schedule_impact?: ImpactLevel | null;
  assigned_to?: string | null;
  assigned_to_email?: string | null;
  assigned_to_org_id?: string | null;
  answer?: string | null;
  answered_by?: string | null;
  answered_by_email?: string | null;
  answered_by_org_id?: string | null;
  status: RFIStatus;
  date_issued?: string | null;   // Auto-set on draft→open
  date_due?: string | null;
  date_answered?: string | null; // Auto-set on answer submission
  date_closed?: string | null;   // Auto-set on →closed
  created_by: string;
  created_at: string;
  updated_at: string;
  file_count_question?: number | null;
  file_count_answer?: number | null;
}

/**
 * API response for list RFIs
 */
export interface ListRFIsResponse {
  success: boolean;
  rfis: RFI[];
  total: number;
  project_id: string;
}

/**
 * API response for single RFI operations (get, create, update, status, answer)
 */
export interface RFIResponse {
  success: boolean;
  rfi: RFI;
  files?: RFIFile[];
  timestamp?: string;
}

/**
 * API response for file list
 */
export interface RFIFilesResponse {
  files: RFIFile[];
  count: number;
}

/**
 * API response for delete RFI
 */
export interface DeleteRFIResponse {
  success: boolean;
  message: string;
}

/**
 * API response for send RFI email
 */
export interface SendRFIEmailResponse {
  success: boolean;
  rfi: RFI;
  sent_at: string;
  sent_to: string;
  message_id?: string;
}

/**
 * Query options for RFI list
 */
export interface RFIListOptions {
  status?: RFIStatus;
  search?: string;
}
